import { type FC, type ReactNode } from 'react'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { Eye, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

interface DataTableActionsProps {
    selectedCount: number
    onViewRow?: () => void
    onDownloadReceipt?: () => void
    downloadLabel?: string
    viewLabel?: string
    customActions?: Array<{
        label: string
        onClick: () => void
        icon?: ReactNode
    }>
    onClearSelection: () => void
    showViewButton?: boolean
    selectionState: CheckedState
    onToggleAll: (value: boolean) => void
}

export const DataTableActions: FC<DataTableActionsProps> = ({
    selectedCount,
    onViewRow,
    onDownloadReceipt,
    downloadLabel = 'Download receipt',
    viewLabel = 'View transaction',
    customActions = [],
    onClearSelection,
    showViewButton = true,
    selectionState,
    onToggleAll,
}) => {
    const canShowViewButton = showViewButton && selectedCount === 1 && onViewRow

    return (
        <div className="flex h-12 flex-wrap items-center gap-3 rounded-t-md bg-muted/60 px-3 text-sm text-foreground">
            <Checkbox
                checked={selectionState}
                onCheckedChange={value => onToggleAll(value === true)}
                className="opacity-90"
                aria-label="Select all rows"
            />
            {/* View transaction (only for single selection) */}
            {canShowViewButton && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onViewRow}
                    className="h-9 rounded-full border-[#5FA8B4]/50 bg-white px-4 text-[#0F172A] shadow-sm hover:border-[#5FA8B4] hover:bg-[#5FA8B4]/10"
                >
                    <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#5FA8B4]/15">
                        <Eye className="h-4 w-4 text-[#5FA8B4]" />
                    </span>
                    {viewLabel}
                </Button>
            )}

            {/* Download receipt */}
            {onDownloadReceipt && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onDownloadReceipt}
                    className="h-9 rounded-full border-[#DC1F4E]/40 bg-white px-4 text-[#0F172A] shadow-sm hover:border-[#DC1F4E]/80 hover:bg-[#DC1F4E]/10"
                >
                    <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#DC1F4E]/15">
                        <Download className="h-4 w-4 text-[#DC1F4E]" />
                    </span>
                    {downloadLabel}
                </Button>
            )}

            {/* Custom actions */}
            {customActions.map((action, index) => (
                <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={action.onClick}
                    className="h-9 rounded-full border-border bg-white px-4 text-[#0F172A] shadow-sm hover:bg-muted/80"
                >
                    {action.icon && (
                        <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                            {action.icon}
                        </span>
                    )}
                    {action.label}
                </Button>
            ))}

            <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="h-8 px-3 text-muted-foreground hover:text-foreground"
            >
                Clear
            </Button>
        </div>
    )
}
